<template>
  <transition name="topbar-slide">
    <div v-if="visible" class="mobile-top-bar">
      <div class="top-bar-content">
        <div class="top-bar-left">
          <button
            v-if="!isDesktop"
            class="icon-button icon-button-back"
            @click="goBack"
            title="Retour"
          >
            <i class="bi bi-arrow-left"></i>
          </button>
          <slot name="left"></slot>
        </div>
        
        <!-- Desktop Navigation -->
        <nav class="desktop-nav" v-if="isDesktop">
          <router-link
            v-for="(item, index) in navItems"
            :key="index"
            :to="item.route"
            class="nav-item"
            active-class="active"
            exact-active-class="active"
            :title="item.title"
          >
            <div class="nav-icon">
              <i :class="item.icon"></i>
            </div>
            <span class="nav-label">{{ item.title }}</span>
          </router-link>
        </nav>
        
        <!-- Mobile Title -->
        <div class="top-bar-title" :title="pageTitle" v-if="!isDesktop">
          <span>{{ pageTitle }}</span>
        </div>
        
        <div class="top-bar-actions">
          <button
            class="icon-button icon-button-call"
            @click="callSupport"
            title="Appeler"
          >
            <i class="bi bi-telephone"></i>
          </button>
          <button
            class="icon-button icon-button-danger"
            @click="logout"
            title="Déconnexion"
          >
            <i class="bi bi-box-arrow-right"></i>
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: "MobileTopBar",
  props: {
    visible: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      isDesktop: false,
      navItems: [
        {
          title: "Accueil",
          icon: "bi bi-house",
          route: { name: "home" },
        },
        {
          title: "Ventes",
          icon: "bi bi-cart",
          route: { name: "sales-list" },
        },
        {
          title: "Paiements",
          icon: "bi bi-credit-card",
          route: { name: "payments-list" },
        },
        {
          title: "Crédits",
          icon: "bi bi-wallet2",
          route: { name: "credits" },
        },
        {
          title: "Clients",
          icon: "bi bi-people",
          route: { name: "customers-list" },
        },
      ],
    };
  },
  computed: {
    pageTitle() {
      const title = this.$route.meta && this.$route.meta.title;
      if (title) {
        return title;
      }
      const name = this.$route.name || "";
      return name
        .replace(/-/g, " ")
        .replace(/_/g, " ")
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/\b\w/g, (l) => l.toUpperCase())
        .trim();
    },
  },
  mounted() {
    this.checkDesktop();
    window.addEventListener("resize", this.checkDesktop);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.checkDesktop);
  },
  methods: {
    checkDesktop() {
      this.isDesktop = window.innerWidth >= 768;
    },
    goBack() {
      // Use router.back() which is the Vue Router recommended way
      if (this.$router.currentRoute.name !== "home") {
        this.$router.back();
      } else {
        // If already on home, try to go to a default route or do nothing
        this.$router.push({ name: "home" }).catch(() => {
          // Ignore navigation errors
        });
      }
    },
    goToCart() {
      if (this.$route.name !== "cart") {
        this.$router.push({ name: "cart" });
      }
    },
    logout() {
      this.$store.dispatch("auth/logout");
    },
    callSupport() {
      window.open("tel:+212600302731", "_self");
    },
  },
};
</script>

<style scoped>
.mobile-top-bar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: linear-gradient(
    135deg,
    var(--primary-dark),
    var(--primary-color)
  );
  color: var(--text-white);
  z-index: 1100;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
  padding-top: env(safe-area-inset-top, 0px);
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.25);
}

.top-bar-content {
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 1rem;
}

.top-bar-left {
  min-width: 40px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;
}

.top-bar-title {
  flex: 1;
  text-align: center;
  font-weight: 600;
  font-size: 1rem;
  letter-spacing: 0.01em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 0 0.5rem;
}

.top-bar-actions {
  min-width: 80px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
}

.icon-button {
  width: 36px;
  height: 36px;
  border-radius: 12px;
  border: none;
  background: rgba(255, 255, 255, 0.12);
  color: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.2s ease;
}

.icon-button:hover {
  background: rgba(255, 255, 255, 0.18);
  transform: translateY(-1px);
}

.icon-button:active {
  transform: translateY(0);
}

.icon-button-danger {
  background: rgba(239, 68, 68, 0.2);
  color: #fca5a5;
}

.icon-button-danger:hover {
  background: rgba(239, 68, 68, 0.3);
  color: #fee2e2;
}

.icon-button-back {
  background: rgba(255, 255, 255, 0.15);
}

.icon-button-back:hover {
  background: rgba(255, 255, 255, 0.22);
}

.icon-button-call {
  background: rgba(59, 130, 246, 0.18);
  color: #bfdbfe;
}

.icon-button-call:hover {
  background: rgba(59, 130, 246, 0.26);
  color: #e0f2fe;
}

.topbar-slide-enter-active,
.topbar-slide-leave-active {
  transition: transform 0.2s ease, opacity 0.2s ease;
}

.topbar-slide-enter,
.topbar-slide-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}

/* Desktop Navigation */
.desktop-nav {
  flex: 1;
  display: none;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0 1rem;
}

.nav-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  padding: 0.5rem 0.75rem;
  border-radius: 12px;
  text-decoration: none;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.75rem;
  font-weight: 600;
  min-width: 70px;
  position: relative;
  overflow: hidden;
  transition: all 0.2s ease;
}

.nav-item::before {
  content: "";
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.1);
  opacity: 0;
  transition: opacity 0.25s ease;
  pointer-events: none;
  border-radius: 12px;
}

.nav-item:hover {
  color: #fff;
  background: rgba(255, 255, 255, 0.15);
  transform: translateY(-2px);
}

.nav-item:hover::before {
  opacity: 1;
}

.nav-item.active {
  color: #fff;
  background: rgba(255, 255, 255, 0.2);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.nav-item.active::before {
  opacity: 1;
}

.nav-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: grid;
  place-items: center;
  background: rgba(255, 255, 255, 0.15);
  transition: inherit;
}

.nav-icon i {
  font-size: 1rem;
  transition: inherit;
}

.nav-item.active .nav-icon {
  background: rgba(255, 255, 255, 0.25);
  transform: scale(1.1);
}

.nav-label {
  text-align: center;
  white-space: nowrap;
  letter-spacing: 0.01em;
  line-height: 1.2;
}

/* Desktop styles */
@media (min-width: 768px) {
  .mobile-top-bar {
    height: 70px;
  }

  .top-bar-content {
    height: 70px;
    padding: 0 1.5rem;
  }

  .top-bar-title {
    display: none;
  }

  .desktop-nav {
    display: flex;
  }

  .top-bar-left {
    min-width: 60px;
  }

  .top-bar-actions {
    min-width: 100px;
  }
}
</style>
